import { definedMap } from "@glideapps/ts-necessities";
import { trackEvent } from "analytics";
import { AppKind } from "common/components/app-kind";
import { getLocalizedString } from "common/components/localization";
import type { LocalizedStringKey } from "common/components/localization/types";
import { logError, logInfo } from "common/debug-print";
import { getFeatureSetting } from "common/feature-settings";
import { defined, glideViralReferralLink } from "common/Support";
import { getInYourFaceBrandingKey, localStorageGetItem, localStorageSetItem } from "support/local-storage";
import randomSample from "lodash/sample";
import { v4 as uuid } from "uuid";

const proportionOfUsers = 0.25;
const numberOfSecondsUntilShown = 60;

// const proportionOfUsers = 1;
// const numberOfSecondsUntilShown = 2;

interface NotSelectedState {
    readonly selected: false;
}

interface SelectedState {
    readonly selected: true;
    readonly shown: boolean;
    readonly secondsInApp: number;
}

type State = NotSelectedState | SelectedState;

let currentState: State | undefined;

export interface InYourFaceBrandingProps {
    readonly imageURL: string;
    readonly heroTextBeforeBold: string;
    readonly heroTextBold: string;
    readonly heroTextAfterBold: string;
    readonly confirmText: string;
    readonly cancelText: string;
    readonly linkURL: string;
    readonly color: string;
}

const heroTextChoices: LocalizedStringKey[] = ["brandingHero1", "brandingHero2"];
const heroConfirmChoices: LocalizedStringKey[] = ["brandingConfirmText1", "brandingConfirmText2"];
const heroCancelChoices: LocalizedStringKey[] = ["brandingCancelText1"];
const colors: Record<string, string> = { white: "#F5F5F5", blue: "#4F5DFF", green: "#3AA657" };

export function initInYourFaceBranding(
    appID: string,
    appKind: AppKind,
    showBranding: (props: InYourFaceBrandingProps, reportResult: (converted: boolean) => void) => void
): void {
    if (!getFeatureSetting("inYourFaceBranding") || appKind === AppKind.Page) return;

    if (currentState !== undefined) return;

    const key = getInYourFaceBrandingKey(appID);
    currentState = definedMap(localStorageGetItem(key), JSON.parse);

    if (currentState === undefined) {
        const selected = Math.random() < proportionOfUsers;
        logInfo("Selected for branding", selected);
        trackEvent("in app branding selected", { app_id: appID, selected });
        if (selected) {
            currentState = { selected: true, shown: false, secondsInApp: 0 };
        } else {
            currentState = { selected: false };
        }

        localStorageSetItem(key, JSON.stringify(currentState));
    }

    if (!currentState.selected || currentState.shown) return;

    const heroTextChoice = defined(randomSample(heroTextChoices));
    const confirmTextChoice = defined(randomSample(heroConfirmChoices));
    const cancelTextChoice = defined(randomSample(heroCancelChoices));
    const colorChoice = defined(randomSample(Object.keys(colors)));

    const heroText = getLocalizedString(heroTextChoice, appKind);

    const branding = {
        id: uuid(),
        hero_image: "/images/branding-image-1.png",
        hero_text: heroTextChoice,
        confirm_text: confirmTextChoice,
        cancel_text: cancelTextChoice,
        style: "default",
        link_url: glideViralReferralLink("popup"),
        color: colors[colorChoice],
    };

    const interval = setInterval(() => {
        if (currentState === undefined || !currentState.selected || currentState.shown) {
            logError("Branding interval handler called incorrectly");
            clearInterval(interval);
            return;
        }

        const secondsInApp = currentState.secondsInApp + 1;
        const shown = secondsInApp >= numberOfSecondsUntilShown;

        currentState = { ...currentState, secondsInApp, shown };
        localStorageSetItem(key, JSON.stringify(currentState));

        if (!shown) return;

        clearInterval(interval);

        trackEvent("in app branding shown", { app_id: appID, ...branding });

        try {
            const [heroTextBeforeBold, heroTextRest] = heroText.split("<b>");
            const [heroTextBold, heroTextAfterBold] = heroTextRest.split("</b>");
            const startTime = Date.now();
            showBranding(
                {
                    imageURL: branding.hero_image,
                    heroTextBeforeBold,
                    heroTextBold,
                    heroTextAfterBold,
                    confirmText: getLocalizedString(branding.confirm_text, appKind),
                    cancelText: getLocalizedString(branding.cancel_text, appKind),
                    linkURL: branding.link_url,
                    color: branding.color,
                },
                converted => {
                    const endTime = Date.now();
                    trackEvent("in app branding clicked", {
                        app_id: appID,
                        ...branding,
                        converted,
                        time_shown: endTime - startTime,
                    });
                }
            );
        } catch (e: unknown) {
            logError("Error showing branding", e);
        }
    }, 1000);
}
